import { useLoaderData, useRevalidator, useRouteError } from "react-router-dom";
import GenericErrorDisplay, { useSetError } from "../generic-error";
import TransmedProcessFormSectionService from "../../service/transmed/process/form/section";
import OutputTransmedFormSection, {
  TransmedFormSectionInput,
} from "../../dtos/transmed/process/form/section";
import { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faAdd,
  faClose,
  faChevronDown,
  faMinus,
  faTrash,
  faChevronUp,
  faCheck,
} from "@fortawesome/free-solid-svg-icons";
import NumberInput from "../../components/inputs/number";
import TextInput from "../../components/inputs/text";
import SwitchInput from "../../components/inputs/checkbox/switch";
import OutputTransmedFormSubsection, {
  TransmedFormSubsectionInput,
} from "../../dtos/transmed/process/form/subsection";
import OutputTransmedFormField, {
  TransmedFormFieldInput,
} from "../../dtos/transmed/process/form/field";
import { CircularProgress } from "@mui/material";
import TransmedProcessFormSubsectionService from "../../service/transmed/process/form/subsection";
import TransmedProcessFormFieldService from "../../service/transmed/process/form/field";
import InputDeleteDialog from "../../components/dialog/delete";
import TransmedProcessService from "../../service/transmed/process";

export const path = "transmed/form";

export async function loader() {
  const sections = new TransmedProcessFormSectionService().read.current();
  const edition = new TransmedProcessService().read.edition();
  return [await sections, (await edition).process_started];
}

export function Component() {
  const revalidator = useRevalidator();
  const [sections, disabled] = useLoaderData() as [
    OutputTransmedFormSection[],
    boolean
  ];

  const sectionService = new TransmedProcessFormSectionService();
  const subsectionService = new TransmedProcessFormSubsectionService();
  const fieldService = new TransmedProcessFormFieldService();

  const [addingSection, setAddingSection] = useState<boolean>(false);
  const [sectionId, setSectionId] = useState<number | null>(null);
  const [sectionTitle, setSectionTitle] = useState<string>("");
  const [sectionWeight, setSectionWeight] = useState<number | null>(null);
  const [sectionIsProportional, setSectionIsProportional] =
    useState<boolean>(false);
  const [sectionProportional, setSectionProportional] = useState<number | null>(
    null
  );

  const [addingSubsection, setAddingSubsection] = useState<boolean>(false);
  const [subsectionId, setSubsectionId] = useState<number | null>(null);
  const [subsectionTitle, setSubsectionTitle] = useState<string>("");

  const [addingField, setAddingField] = useState<boolean>(false);
  const [fieldId, setFieldId] = useState<number | null>(null);
  const [fieldTitle, setFieldTitle] = useState<string>("");
  const [fieldSubtitle, setFieldSubtitle] = useState<string>("");
  const [fieldPoints, setFieldPoints] = useState<number | null>(null);
  const [fieldQuantitative, setFieldQuantitative] = useState<boolean>(false);
  const [fieldUnitSingular, setFieldUnitSingular] = useState<string>("");
  const [fieldUnitPlural, setFieldUnitPlural] = useState<string>("");

  const setErrorOnly = useSetError();
  const setError = (error: unknown) => {
    setErrorOnly(error);
    revalidator.revalidate();
  };
  const [icon, setIcon] = useState<JSX.Element | null>(null);
  const [dialogCallback, setDialogCallback] = useState<
    (() => Promise<void> | void) | null
  >(null);

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "stretch",
        flex: 1,
        justifyContent: "flex-start",
        gap: "var(--gap-medium)",
        position: "relative",
      }}
    >
      <h1>Formular înscriere transmed</h1>
      <button
        className={"primary button " + (addingSection ? "outlined" : "text")}
        style={{ alignSelf: "flex-start" }}
        onClick={() => {
          setAddingSection((value) => !value);

          setSectionId(null);
          setSectionTitle("");
          setSectionWeight(null);
          setSectionIsProportional(false);
          setSectionProportional(100);
        }}
        {...{ disabled }}
      >
        <FontAwesomeIcon icon={addingSection ? faMinus : faAdd} /> Secțiune nouă
      </button>
      {[
        ...(addingSection
          ? [new TransmedFormSectionInput("Secțiune nouă", 0, null)]
          : []),
        ...sections,
      ].map((section) => (
        <div
          key={section instanceof OutputTransmedFormSection ? section.id : -1}
          className="surface"
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "stretch",
            justifyContent: "flex-start",
            gap: "var(--gap-medium)",
          }}
        >
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              flexWrap: "wrap",
              alignItems: "center",
              justifyContent: "flex-end",
              gap: "var(--gap-xs)",
            }}
          >
            <h3 style={{ flex: 1 }}>{section.title}</h3>
            {section instanceof OutputTransmedFormSection ? (
              <button
                className="text primary button"
                onClick={() => {
                  setAddingSection(false);
                  setAddingSubsection(false);
                  setAddingField(false);

                  setSubsectionId(null);
                  setFieldId(null);

                  setSectionId((id) => (id !== section.id ? section.id : null));
                  setSectionWeight(section.weight);
                  setSectionTitle(section.title);
                  setSectionIsProportional(section.proportional !== null);
                  setSectionProportional(section.proportional ?? 100);
                }}
              >
                <FontAwesomeIcon
                  icon={section.id === sectionId ? faChevronUp : faChevronDown}
                />
              </button>
            ) : (
              <button
                className="text primary button"
                onClick={() => {
                  setAddingSection(false);
                }}
              >
                <FontAwesomeIcon icon={faClose} />
              </button>
            )}
          </div>
          {((section instanceof OutputTransmedFormSection &&
            section.id === sectionId) ||
            (section instanceof TransmedFormSectionInput &&
              sectionId === null)) && (
            <>
              <form
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "stretch",
                  justifyContent: "flex-start",
                  gap: "var(--gap-small)",
                }}
                onSubmit={async (e) => {
                  e.preventDefault();
                  setError("");
                  setIcon(<CircularProgress />);
                  try {
                    if (sectionWeight === null)
                      throw new TypeError("Section weight must not be empty!");

                    if (sectionId === null)
                      await sectionService.create.section(
                        sectionTitle,
                        sectionWeight,
                        sectionIsProportional ? sectionProportional : null
                      );
                    else
                      await sectionService.update.section(
                        sectionId,
                        sectionTitle,
                        sectionWeight,
                        sectionIsProportional ? sectionProportional : null
                      );

                    setIcon(<FontAwesomeIcon icon={faCheck} />);
                  } catch (error) {
                    setIcon(null);
                    setError(error);
                  } finally {
                    revalidator.revalidate();
                  }
                }}
              >
                <TextInput
                  style={{ flex: 1 }}
                  required
                  value={sectionTitle}
                  onChange={(e) => setSectionTitle(e.target.value)}
                  label="Numele secțiunii"
                  {...{ disabled }}
                />
                <NumberInput
                  style={{ flex: 1 }}
                  required
                  label="Ponderea secțiunii"
                  value={sectionWeight ?? ""}
                  onChange={(e) =>
                    setSectionWeight(
                      isNaN(parseInt(e.target.value))
                        ? null
                        : parseInt(e.target.value)
                    )
                  }
                  {...{ disabled }}
                />
                <SwitchInput
                  style={{ flex: 1 }}
                  label="Punctaj maxim predefinit"
                  checked={sectionIsProportional}
                  onChange={(e) => setSectionIsProportional(e.target.checked)}
                  {...{ disabled }}
                />
                {sectionIsProportional && (
                  <NumberInput
                    style={{ flex: 1 }}
                    required
                    label="Punctaj maxim"
                    value={sectionProportional ?? ""}
                    onChange={(e) =>
                      setSectionProportional(
                        isNaN(parseInt(e.target.value))
                          ? null
                          : parseInt(e.target.value)
                      )
                    }
                    {...{ disabled }}
                  />
                )}
                <div
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    flexWrap: "wrap",
                    alignItems: "center",
                    justifyContent: "flex-start",
                    gap: "var(--gap-small)",
                  }}
                >
                  <button
                    type="submit"
                    className="primary button"
                    style={{ alignSelf: "flex-start" }}
                    {...{ disabled }}
                  >
                    Salvează
                  </button>
                  {section instanceof TransmedFormSectionInput ? (
                    <button
                      type="button"
                      className="secondary button"
                      onClick={() => setAddingSection(false)}
                    >
                      <FontAwesomeIcon icon={faClose} /> Anulează
                    </button>
                  ) : (
                    <>
                      <button
                        type="button"
                        className="primary button"
                        style={{
                          alignSelf: "center",
                          ...{ "--color": "red" },
                        }}
                        {...{ disabled }}
                        onClick={(e) => {
                          e.preventDefault();

                          setDialogCallback(() => async () => {
                            if (section.subsections.length !== 0) {
                              setError(
                                "Trebuie să ștergi întâi subsecțiunile componente!"
                              );
                              return;
                            }

                            setError("");
                            setIcon(<CircularProgress />);
                            try {
                              if (sectionId === null)
                                throw new TypeError(
                                  "Section id must not be empty!"
                                );

                              await sectionService.delete.section(sectionId);

                              revalidator.revalidate();
                              setIcon(<FontAwesomeIcon icon={faCheck} />);
                            } catch (error) {
                              setIcon(null);
                              setError(error);
                            }
                          });
                        }}
                      >
                        <FontAwesomeIcon icon={faTrash} /> Șterge secțiunea
                      </button>
                      <button
                        type="button"
                        className={
                          "primary button " +
                          (addingSubsection ? "outlined" : "text")
                        }
                        onClick={() => {
                          setAddingSubsection((value) => !value);

                          setSubsectionId(null);
                          setSubsectionTitle("");
                        }}
                        {...{ disabled }}
                      >
                        <FontAwesomeIcon
                          icon={addingSubsection ? faMinus : faAdd}
                        />{" "}
                        Subsecțiune nouă
                      </button>
                    </>
                  )}
                  {revalidator.state === "idle" ? icon : <CircularProgress />}
                </div>
              </form>
              {section instanceof OutputTransmedFormSection &&
                (section.subsections.length > 0 || addingSubsection) && (
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "stretch",
                      justifyContent: "flex-start",
                      gap: "var(--gap-xs)",
                    }}
                  >
                    {[
                      ...(addingSubsection
                        ? [
                            new TransmedFormSubsectionInput(
                              "Subsecțiune nouă",
                              -1
                            ),
                          ]
                        : []),
                      ...section.subsections,
                    ].map((subsection) => (
                      <div
                        key={
                          subsection instanceof OutputTransmedFormSubsection
                            ? subsection.id
                            : -1
                        }
                        className="surface"
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "stretch",
                          justifyContent: "flex-start",
                          gap: "var(--gap-medium)",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "row",
                            flexWrap: "wrap",
                            alignItems: "center",
                            justifyContent: "flex-end",
                            gap: "var(--gap-xs)",
                          }}
                        >
                          <h4 style={{ flex: 1 }}>{subsection.title}</h4>
                          {subsection instanceof
                          OutputTransmedFormSubsection ? (
                            <button
                              className="text primary button"
                              onClick={() => {
                                setAddingSubsection(false);
                                setAddingField(false);

                                setFieldId(null);

                                setSubsectionId((id) =>
                                  id !== subsection.id ? subsection.id : null
                                );
                                setSubsectionTitle(subsection.title);
                              }}
                            >
                              <FontAwesomeIcon
                                icon={
                                  subsection.id === subsectionId
                                    ? faChevronUp
                                    : faChevronDown
                                }
                              />
                            </button>
                          ) : (
                            <button
                              className="text primary button"
                              onClick={() => {
                                setAddingSubsection(false);
                              }}
                            >
                              <FontAwesomeIcon icon={faClose} />
                            </button>
                          )}
                        </div>
                        {((subsection instanceof OutputTransmedFormSubsection &&
                          subsection.id === subsectionId) ||
                          (subsection instanceof TransmedFormSubsectionInput &&
                            subsectionId === null)) && (
                          <>
                            <form
                              style={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "stretch",
                                justifyContent: "flex-start",
                                gap: "var(--gap-small)",
                              }}
                              onSubmit={async (e) => {
                                e.preventDefault();
                                setError("");
                                setIcon(<CircularProgress />);
                                try {
                                  if (sectionId === null)
                                    throw new TypeError(
                                      "section id must not be empty!"
                                    );

                                  if (subsectionId === null)
                                    await subsectionService.create.subsection(
                                      subsectionTitle,
                                      sectionId
                                    );
                                  else
                                    await subsectionService.update.subsection(
                                      subsectionId,
                                      subsectionTitle,
                                      sectionId
                                    );

                                  setIcon(<FontAwesomeIcon icon={faCheck} />);
                                } catch (error) {
                                  setIcon(null);
                                  setError(error);
                                } finally {
                                  revalidator.revalidate();
                                }
                              }}
                            >
                              <TextInput
                                style={{ flex: 1 }}
                                required
                                value={subsectionTitle}
                                onChange={(e) =>
                                  setSubsectionTitle(e.target.value)
                                }
                                label="Numele subsecțiunii"
                                {...{ disabled }}
                              />
                              <div
                                style={{
                                  display: "flex",
                                  flexDirection: "row",
                                  flexWrap: "wrap",
                                  alignItems: "center",
                                  justifyContent: "flex-start",
                                  gap: "var(--gap-small)",
                                }}
                              >
                                <button
                                  type="submit"
                                  className="primary button"
                                  style={{ alignSelf: "flex-start" }}
                                  {...{ disabled }}
                                >
                                  Salvează
                                </button>
                                {subsection instanceof
                                TransmedFormSubsectionInput ? (
                                  <button
                                    type="button"
                                    className="secondary button"
                                    style={{ alignSelf: "flex-start" }}
                                    onClick={() => setAddingSubsection(false)}
                                  >
                                    <FontAwesomeIcon icon={faClose} /> Anulează
                                  </button>
                                ) : (
                                  <>
                                    <button
                                      type="button"
                                      className="primary button"
                                      style={{
                                        alignSelf: "center",
                                        ...{ "--color": "red" },
                                      }}
                                      {...{ disabled }}
                                      onClick={async (e) => {
                                        e.preventDefault();

                                        setDialogCallback(() => async () => {
                                          if (subsection.fields.length !== 0) {
                                            setError(
                                              "Trebuie să ștergi întâi câmpurile componente!"
                                            );
                                            return;
                                          }

                                          setError("");
                                          setIcon(<CircularProgress />);
                                          try {
                                            if (subsectionId === null)
                                              throw new TypeError(
                                                "Section id must not be empty!"
                                              );

                                            await subsectionService.delete.subsection(
                                              subsectionId
                                            );

                                            revalidator.revalidate();
                                            setIcon(
                                              <FontAwesomeIcon icon={faCheck} />
                                            );
                                          } catch (error) {
                                            setIcon(null);
                                            setError(error);
                                          }
                                        });
                                      }}
                                    >
                                      <FontAwesomeIcon icon={faTrash} /> Șterge
                                      subsecțiunea
                                    </button>
                                    <button
                                      type="button"
                                      className={
                                        "primary button " +
                                        (addingField ? "outlined" : "text")
                                      }
                                      style={{ alignSelf: "flex-start" }}
                                      {...{ disabled }}
                                      onClick={() => {
                                        setAddingField((value) => !value);

                                        setFieldId(null);
                                        setFieldTitle("");
                                        setFieldSubtitle("");
                                        setFieldPoints(null);
                                        setFieldQuantitative(false);
                                        setFieldUnitSingular("");
                                        setFieldUnitPlural("");
                                      }}
                                    >
                                      <FontAwesomeIcon
                                        icon={addingField ? faMinus : faAdd}
                                      />{" "}
                                      Câmp nou
                                    </button>
                                  </>
                                )}
                                {revalidator.state === "idle" ? (
                                  icon
                                ) : (
                                  <CircularProgress />
                                )}
                              </div>
                            </form>
                            {subsection instanceof
                              OutputTransmedFormSubsection &&
                              (subsection.fields.length > 0 || addingField) && (
                                <div
                                  style={{
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "stretch",
                                    justifyContent: "flex-start",
                                    gap: "var(--gap-xs)",
                                  }}
                                >
                                  {[
                                    ...(addingField
                                      ? [
                                          new TransmedFormFieldInput(
                                            "Câmp nou",
                                            "",
                                            0,
                                            false,
                                            "",
                                            "",
                                            -1
                                          ),
                                        ]
                                      : []),
                                    ...subsection.fields,
                                  ].map((field) => (
                                    <div
                                      key={
                                        field instanceof OutputTransmedFormField
                                          ? field.id
                                          : -1
                                      }
                                      className="surface"
                                      style={{
                                        display: "flex",
                                        flexDirection: "column",
                                        alignItems: "stretch",
                                        justifyContent: "flex-start",
                                        gap: "var(--gap-medium)",
                                      }}
                                    >
                                      <div
                                        style={{
                                          display: "flex",
                                          flexDirection: "row",
                                          flexWrap: "wrap",
                                          alignItems: "center",
                                          justifyContent: "flex-end",
                                          gap: "var(--gap-xs)",
                                        }}
                                      >
                                        <b
                                          style={{
                                            flex: 1,
                                          }}
                                        >
                                          {field.title}
                                        </b>
                                        {field instanceof
                                        OutputTransmedFormField ? (
                                          <button
                                            className="text primary button"
                                            onClick={() => {
                                              setAddingField(false);

                                              setFieldId((id) =>
                                                id !== field.id
                                                  ? field.id
                                                  : null
                                              );
                                              setFieldTitle(field.title);
                                              setFieldSubtitle(field.subtitle);
                                              setFieldPoints(field.points);
                                              setFieldQuantitative(
                                                field.quantitative
                                              );
                                              setFieldUnitSingular(
                                                field.unit_singular
                                              );
                                              setFieldUnitPlural(
                                                field.unit_plural
                                              );
                                            }}
                                          >
                                            <FontAwesomeIcon
                                              icon={
                                                field.id === fieldId
                                                  ? faChevronUp
                                                  : faChevronDown
                                              }
                                            />
                                          </button>
                                        ) : (
                                          <button
                                            className="text primary button"
                                            onClick={() => {
                                              setAddingField(false);
                                            }}
                                          >
                                            <FontAwesomeIcon icon={faClose} />
                                          </button>
                                        )}
                                      </div>
                                      {((field instanceof
                                        OutputTransmedFormField &&
                                        field.id === fieldId) ||
                                        (field instanceof
                                          TransmedFormFieldInput &&
                                          fieldId === null)) && (
                                        <>
                                          <form
                                            style={{
                                              display: "flex",
                                              flexDirection: "column",
                                              alignItems: "stretch",
                                              justifyContent: "flex-start",
                                              gap: "var(--gap-small)",
                                            }}
                                            onSubmit={async (e) => {
                                              e.preventDefault();
                                              setError("");
                                              setIcon(<CircularProgress />);
                                              try {
                                                if (subsectionId === null)
                                                  throw new TypeError(
                                                    "subsection id must not be empty!"
                                                  );
                                                if (fieldPoints === null)
                                                  throw new TypeError(
                                                    "fieldPoints must not be empty!"
                                                  );

                                                if (fieldId === null)
                                                  await fieldService.create.field(
                                                    fieldTitle,
                                                    fieldSubtitle,
                                                    fieldPoints,
                                                    fieldQuantitative,
                                                    fieldUnitSingular,
                                                    fieldUnitPlural,
                                                    subsectionId
                                                  );
                                                else
                                                  await fieldService.update.field(
                                                    fieldId,
                                                    fieldTitle,
                                                    fieldSubtitle,
                                                    fieldPoints,
                                                    fieldQuantitative,
                                                    fieldUnitSingular,
                                                    fieldUnitPlural,
                                                    subsectionId
                                                  );

                                                setIcon(
                                                  <FontAwesomeIcon
                                                    icon={faCheck}
                                                  />
                                                );
                                              } catch (error) {
                                                setIcon(null);
                                                setError(error);
                                              } finally {
                                                revalidator.revalidate();
                                              }
                                            }}
                                          >
                                            <TextInput
                                              style={{ flex: 1 }}
                                              required
                                              value={fieldTitle}
                                              onChange={(e) =>
                                                setFieldTitle(e.target.value)
                                              }
                                              label="Numele câmpului"
                                              {...{ disabled }}
                                            />
                                            <TextInput
                                              style={{ flex: 1 }}
                                              required
                                              value={fieldSubtitle}
                                              onChange={(e) =>
                                                setFieldSubtitle(e.target.value)
                                              }
                                              label="Descrierea câmpului"
                                              {...{ disabled }}
                                            />
                                            <NumberInput
                                              style={{ flex: 1 }}
                                              required
                                              value={fieldPoints ?? ""}
                                              onChange={(e) =>
                                                setFieldPoints(
                                                  isNaN(
                                                    parseFloat(e.target.value)
                                                  )
                                                    ? null
                                                    : parseFloat(e.target.value)
                                                )
                                              }
                                              label="Puncte asociate"
                                              {...{ disabled }}
                                            />
                                            <SwitchInput
                                              style={{ flex: 1 }}
                                              label="Câmp măsurabil"
                                              checked={fieldQuantitative}
                                              onChange={(e) =>
                                                setFieldQuantitative(
                                                  e.target.checked
                                                )
                                              }
                                              {...{ disabled }}
                                            />
                                            {fieldQuantitative && (
                                              <>
                                                <TextInput
                                                  style={{ flex: 1 }}
                                                  required
                                                  value={fieldUnitSingular}
                                                  onChange={(e) =>
                                                    setFieldUnitSingular(
                                                      e.target.value
                                                    )
                                                  }
                                                  label="Unitate de măsură"
                                                  {...{ disabled }}
                                                />
                                                <TextInput
                                                  style={{ flex: 1 }}
                                                  required
                                                  value={fieldUnitPlural}
                                                  onChange={(e) =>
                                                    setFieldUnitPlural(
                                                      e.target.value
                                                    )
                                                  }
                                                  label="Unitate de măsură (plural)"
                                                  {...{ disabled }}
                                                />
                                              </>
                                            )}
                                            <div
                                              style={{
                                                display: "flex",
                                                flexDirection: "row",
                                                flexWrap: "wrap",
                                                alignItems: "center",
                                                justifyContent: "flex-start",
                                                gap: "var(--gap-small)",
                                              }}
                                            >
                                              <button
                                                type="submit"
                                                className="primary button"
                                                style={{
                                                  alignSelf: "flex-start",
                                                }}
                                                {...{ disabled }}
                                              >
                                                Salvează
                                              </button>
                                              {field instanceof
                                              TransmedFormFieldInput ? (
                                                <button
                                                  type="button"
                                                  className="secondary button"
                                                  style={{
                                                    alignSelf: "flex-start",
                                                  }}
                                                  onClick={() =>
                                                    setAddingField(false)
                                                  }
                                                >
                                                  <FontAwesomeIcon
                                                    icon={faClose}
                                                  />{" "}
                                                  Anulează
                                                </button>
                                              ) : (
                                                <button
                                                  type="button"
                                                  className="primary button"
                                                  style={{
                                                    alignSelf: "center",
                                                    ...{ "--color": "red" },
                                                  }}
                                                  {...{ disabled }}
                                                  onClick={async (e) => {
                                                    e.preventDefault();

                                                    setDialogCallback(
                                                      () => async () => {
                                                        setError("");
                                                        setIcon(
                                                          <CircularProgress />
                                                        );
                                                        try {
                                                          if (fieldId === null)
                                                            throw new TypeError(
                                                              "Section id must not be empty!"
                                                            );

                                                          await fieldService.delete.field(
                                                            fieldId
                                                          );

                                                          revalidator.revalidate();
                                                          setIcon(
                                                            <FontAwesomeIcon
                                                              icon={faCheck}
                                                            />
                                                          );
                                                        } catch (error) {
                                                          setIcon(null);
                                                          setError(error);
                                                        }
                                                      }
                                                    );
                                                  }}
                                                >
                                                  <FontAwesomeIcon
                                                    icon={faTrash}
                                                  />{" "}
                                                  Șterge câmpul
                                                </button>
                                              )}
                                              {revalidator.state === "idle" ? (
                                                icon
                                              ) : (
                                                <CircularProgress />
                                              )}
                                            </div>
                                          </form>
                                        </>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              )}
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                )}
            </>
          )}
        </div>
      ))}
      <InputDeleteDialog
        dialogCallback={dialogCallback}
        onClose={() => setDialogCallback(null)}
      />
    </div>
  );
}
Component.displayName = "page-account-transmed-form";

export function ErrorBoundary() {
  const error = useRouteError();
  return <GenericErrorDisplay error={error} />;
}
ErrorBoundary.displayName = "page-account-transmed-form-error";
